<div class="offer-activation-wizard">
  <div class="wizard-header">
    <div>
      <p class="wizard-kicker">Activation d'offre</p>
      <h1>Structurer et activer l’offre</h1>
      <p class="wizard-subtitle">
        Préparez la souscription, le nombre de collaborateurs et la facturation depuis un parcours guidé.
      </p>
    </div>
    <app-icon type="close" class="close-icon" (click)="close()"></app-icon>
  </div>

  <div class="wizard-steps">
    <div
      *ngFor="let step of steps; let index = index; let last = last"
      class="wizard-step"
      [class.is-active]="index === currentStepIndex"
      [class.is-complete]="index < currentStepIndex">
      <span class="wizard-step-badge">
        <ng-container *ngIf="index < currentStepIndex; else stepNumber">
          <app-icon type="check" class="wizard-step-check" [size]="28"></app-icon>
        </ng-container>
        <ng-template #stepNumber>{{ index + 1 }}</ng-template>
      </span>
      <div class="wizard-step-copy">
        <strong>{{ getStepLabel(step) }}</strong>
      </div>
      <span class="wizard-step-line" *ngIf="!last"></span>
    </div>
  </div>

  <p class="wizard-error" *ngIf="errorMessage">{{ errorMessage }}</p>

  <section class="wizard-panel">
    <div class="wizard-panel-head">
      <div class="wizard-panel-step">
        <span>{{ currentStepIndex + 1 }}</span>
      </div>
      <div class="wizard-panel-copy">
        <h2>{{ currentStepTitle }}</h2>
        <p>{{ currentStepDescription }}</p>
      </div>
    </div>

    <div
      class="wizard-panel-body"
      [class.is-forward]="transitionDirection === 'forward'"
      [class.is-backward]="transitionDirection === 'backward'">
      <section class="wizard-section wizard-scene" *ngIf="currentStep === 'organization'">
        <app-input
          title="Organisation"
          [dropdownData]="organizationLabels"
          [inputField]="organizationControl"
          placeholder="Sélectionner une organisation">
        </app-input>

        <div class="selection-hint">
          Le checkout et la facturation seront préparés pour l’organisation sélectionnée.
        </div>
      </section>

      <section class="wizard-section wizard-scene" *ngIf="currentStep === 'offer'">
        <div class="loading-state" *ngIf="loadingOrganization">Chargement de l’organisation…</div>

        <div *ngIf="!loadingOrganization" class="form-grid">
          <app-input
            title="Offre"
            [dropdownData]="managedPlanNames"
            [inputField]="planControl"
          ></app-input>

          <app-input
            title="Nombre de collaborateurs"
            type="number"
            [inputField]="seatCountControl"
            [min]="1"
            placeholder="1"
          ></app-input>
        </div>

        <div class="selection-hint" *ngIf="!loadingOrganization">
          Paliers de tarification : jusqu&apos;à 50, de 51 à 500, puis 501 et plus.
        </div>

        <div class="context-card" *ngIf="organization">
          <div>
            <span class="context-label">Organisation</span>
            <strong>{{ currentOrganizationName }}</strong>
          </div>
          <div>
            <span class="context-label">Offre active</span>
            <strong>{{ currentOfferName }}</strong>
          </div>
          <div>
            <span class="context-label">Abonnement actuel</span>
            <strong>{{ hasActiveManagedOffer ? 'Déjà actif' : 'Aucun abonnement managé actif' }}</strong>
          </div>
          <div>
            <span class="context-label">Montant estimé</span>
            <strong>{{ selectedAnnualPricePerSeatCents ? formatCurrency(selectedAnnualAmountCents) : 'Sans abonnement annuel' }}</strong>
          </div>
        </div>
      </section>

      <section class="wizard-section wizard-scene" *ngIf="currentStep === 'summary'">
        <div class="checkout-banner" *ngIf="pendingSubscriptionCheckoutUrl">
          <p class="banner-kicker">Paiement déjà préparé</p>
          <h3>Un checkout d’activation existe déjà</h3>
          <p>Reprenez ce paiement au lieu de créer un nouveau flux pour cette organisation.</p>
        </div>

        <div class="summary-grid">
          <div class="summary-item">
            <span class="summary-label">Organisation</span>
            <strong>{{ currentOrganizationName }}</strong>
          </div>
          <div class="summary-item">
            <span class="summary-label">Offre choisie</span>
            <strong>{{ selectedManagedPlanCode ? selectedManagedPlan?.name : 'Aucune offre sélectionnée' }}</strong>
          </div>
          <div class="summary-item">
            <span class="summary-label">Nombre de collaborateurs</span>
            <strong>{{ selectedSeatCount }} collaborateur{{ selectedSeatCount > 1 ? 's' : '' }}</strong>
          </div>
          <div class="summary-item">
            <span class="summary-label">Montant annuel</span>
            <strong>{{ selectedAnnualPricePerSeatCents ? formatCurrency(selectedAnnualAmountCents) : 'Sans abonnement annuel' }}</strong>
          </div>
          <div class="summary-item">
            <span class="summary-label">Parcours inclus</span>
            <strong>{{ selectedIncludedJourneysCount }}</strong>
          </div>
          <div class="summary-item">
            <span class="summary-label">Prix du parcours complémentaire</span>
            <strong>{{ selectedComplementaryJourneyPriceCents ? formatCurrency(selectedComplementaryJourneyPriceCents) + ' / parcours' : 'Non disponible' }}</strong>
          </div>
          <div class="summary-item">
            <span class="summary-label">Fin d’abonnement estimée</span>
            <strong>{{ selectedOfferNextRenewalLabel }}</strong>
          </div>
          <div class="summary-item">
            <span class="summary-label">Facturation</span>
            <strong>{{ pendingSubscriptionCheckoutUrl ? 'Paiement à reprendre' : selectedAnnualPricePerSeatCents ? 'Checkout Sellsy à générer' : 'Activation immédiate sans paiement' }}</strong>
          </div>
        </div>

        <p class="inline-alert" *ngIf="hasActiveManagedOffer">
          Cette organisation dispose déjà d’une offre managée active. Pour l&apos;évolution d&apos;offre, des sièges ou une résiliation à échéance, utilisez la page Crédits et offre.
        </p>
      </section>
    </div>

    <div class="wizard-footer">
      <app-button
        type="button"
        [outline]="true"
        [primary]="true"
        *ngIf="currentStepIndex > 0"
        (onClick)="previous()">
        Retour
      </app-button>

      <app-button
        type="button"
        [outline]="true"
        *ngIf="currentStep === 'summary' && hasActiveManagedOffer"
        (onClick)="goToOrganizationUnits()">
        Ouvrir Crédits et offre
      </app-button>

      <app-button
        type="button"
        [primary]="true"
        *ngIf="currentStep !== 'summary'"
        [disabled]="!canGoNext"
        (onClick)="next()">
        Continuer
      </app-button>

      <app-button
        type="button"
        [primary]="true"
        *ngIf="currentStep === 'summary' && !hasActiveManagedOffer"
        [disabled]="!canConfirmActivation"
        (onClick)="submit()">
        {{ submitting ? 'Préparation…' : pendingSubscriptionCheckoutUrl ? 'Reprendre le paiement' : 'Confirmer l’activation' }}
      </app-button>
    </div>
  </section>
</div>
